import React, { useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";

const GENERATE_URL = `${API_BASE_URL}/saledata/api/module/tax-invoice/generate`;

function getToken() {
    try {
        const user = JSON.parse(localStorage.getItem("user"));
        return user?.token || "";
    } catch { return ""; }
}

// Reads a JSON error message out of an axios blob-response error, since responseType:
// 'blob' makes even a 4xx body arrive as a Blob instead of parsed JSON/text.
async function readErrorMessage(error) {
    const data = error?.response?.data;
    if (data instanceof Blob) {
        try {
            const text = await data.text();
            try { return JSON.parse(text)?.message || text; } catch { return text; }
        } catch { /* fall through */ }
    }
    return error?.message || "สร้างใบกำกับภาษีไม่สำเร็จ";
}

export default function TaxInvoiceBulk() {
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [salesFactor, setSalesFactor] = useState("");
    // "TOKYO_ONLY" matches the backend's default (null/omitted = same filter) — anyone not
    // touching this control gets today's already-shipped behavior unchanged.
    const [agentScope, setAgentScope] = useState("TOKYO_ONLY");
    // Group 2 (values adjustable via salesFactor, invoiceNo stays fixed) and Group 3 (fully
    // locked — format only, nothing adjusted) are two separate upload areas per Vector's
    // explicit confirmation, not one upload with a toggle.
    const [adjustableFiles, setAdjustableFiles] = useState([]);
    const [lockedFiles, setLockedFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    function handleAdjustableFilesChange(e) {
        setAdjustableFiles(Array.from(e.target.files || []));
    }

    function removeAdjustableFile(index) {
        setAdjustableFiles(prev => prev.filter((_, i) => i !== index));
    }

    function handleLockedFilesChange(e) {
        setLockedFiles(Array.from(e.target.files || []));
    }

    function removeLockedFile(index) {
        setLockedFiles(prev => prev.filter((_, i) => i !== index));
    }

    async function generate() {
        setError("");
        const hasFiles = adjustableFiles.length > 0 || lockedFiles.length > 0;
        if ((startDate || endDate) && (!startDate || !endDate)) {
            setError("กรุณาเลือกวันเริ่มต้นและวันสิ้นสุดให้ครบทั้งสองช่อง");
            return;
        }
        if (!startDate && !endDate && !hasFiles) {
            setError("กรุณาเลือกช่วงวันที่ หรืออัพโหลดไฟล์ใบกำกับภาษีจริงอย่างน้อย 1 ไฟล์");
            return;
        }
        if (startDate && endDate && endDate < startDate) {
            setError("วันสิ้นสุดต้องไม่ก่อนวันเริ่มต้น");
            return;
        }

        setLoading(true);
        try {
            const body = { agentScope };
            // Omit blank dates entirely (rather than sending "") — same pattern as salesFactor
            // below — so an uploaded-invoices-only request skips the date range, not sends it as
            // empty strings.
            if (startDate) body.startDate = startDate;
            if (endDate) body.endDate = endDate;
            if (salesFactor !== "") {
                body.salesFactor = parseFloat(salesFactor);
            }
            // multipart/form-data — the "request" part must carry an explicit application/json
            // content-type (a plain string part would arrive un-parsed) so Spring's
            // @RequestPart("request") TaxInvoiceGenerateRequest can deserialize it;
            // "adjustableFiles"/"lockedFiles" are each repeated, 0-N real invoice PDFs. Do NOT
            // set a Content-Type header manually here — axios/the browser needs to add the
            // multipart boundary itself.
            const formData = new FormData();
            formData.append("request", new Blob([JSON.stringify(body)], { type: "application/json" }));
            adjustableFiles.forEach(file => formData.append("adjustableFiles", file));
            lockedFiles.forEach(file => formData.append("lockedFiles", file));

            const response = await axios.post(
                GENERATE_URL,
                formData,
                {
                    headers: { Authorization: `Bearer ${getToken()}` },
                    responseType: "blob",
                }
            );

            const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
            const link = document.createElement("a");
            link.href = url;
            link.download = (startDate && endDate)
                ? `tax-invoices-${startDate}_${endDate}.pdf`
                : `tax-invoices-uploaded-${adjustableFiles.length + lockedFiles.length}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            setError(await readErrorMessage(e));
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="btc-container">
            <h1>ใบกำกับภาษีอย่างย่อ — สร้างเป็นชุด (Bulk)</h1>

            <div className="btc-card" style={{ padding: "16px 24px", marginBottom: 16 }}>
                <h3 style={{ marginTop: 0 }}>เลือกช่วงวันที่ (ไม่บังคับถ้าอัพโหลดไฟล์อย่างเดียว)</h3>
                {error && <p className="error">{error}</p>}
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <input
                        type="date"
                        value={startDate}
                        onChange={e => setStartDate(e.target.value)}
                        style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid #E5E5E5" }}
                    />
                    <span>ถึง</span>
                    <input
                        type="date"
                        value={endDate}
                        onChange={e => setEndDate(e.target.value)}
                        style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid #E5E5E5" }}
                    />
                    <span>สัดส่วนยอดขาย (0.1-1.0)</span>
                    <input
                        type="number"
                        min="0.1"
                        max="1.0"
                        step="0.1"
                        placeholder="เต็ม 100%"
                        value={salesFactor}
                        onChange={e => setSalesFactor(e.target.value)}
                        style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid #E5E5E5", width: 90 }}
                    />
                    <span>ตัวแทนขาย (Agent)</span>
                    <select
                        value={agentScope}
                        onChange={e => setAgentScope(e.target.value)}
                        style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid #E5E5E5" }}
                    >
                        <option value="TOKYO_ONLY">เฉพาะ Tokyo</option>
                        <option value="ALL_AGENTS">รวม agent ทั้งหมด</option>
                        <option value="NO_AGENTS">ไม่รวม agent เลย</option>
                    </select>
                    <button className="btc-btn btn-sm" style={{ backgroundColor: "#343a40" }} onClick={generate} disabled={loading}>
                        {loading ? "กำลังสร้าง..." : "สร้างใบกำกับภาษี"}
                    </button>
                </div>
                <p style={{ margin: "8px 0 0", color: "#666", fontSize: 13 }}>
                    สัดส่วนยอดขายใช้กับข้อมูลจากฐานข้อมูล (ตามช่วงวันที่) และไฟล์ที่อัพโหลดแบบ "ปรับยอดได้" ด้านล่างเท่านั้น
                    — ไม่มีผลกับไฟล์ที่อัพโหลดแบบ "ล็อกทั้งหมด"
                </p>

                <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #E5E5E5" }}>
                    <p style={{
                        margin: "0 0 12px", padding: "8px 12px", borderRadius: 4,
                        backgroundColor: "#fff3cd", border: "1px solid #ffe08a", color: "#664d03",
                    }}>
                        ⚠ หมายเหตุ: รายการ "กระสอบน้ำแข็ง" ใช้ชื่อ hardcode ไว้ในโค้ด backend
                        (อิงจากราคาต่อหน่วย = 10 บาทพอดี ซึ่งเป็นราคาคงที่ของกระสอบน้ำแข็งในปัจจุบัน)
                        เพราะไม่มีอยู่ใน product catalog ของ seller-module — ถ้าราคานี้เปลี่ยน หรือมีการเพิ่มรายการนี้เข้า
                        catalog จริงในอนาคต ต้องแก้โค้ดใน backend (TaxInvoicePdfParser) ด้วย ไม่ใช่แค่หน้านี้
                        (ใช้ได้กับไฟล์ที่อัพโหลดทั้งสองแบบด้านล่าง)
                    </p>

                    <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                        <div style={{ flex: "1 1 320px", minWidth: 280 }}>
                            <h3 style={{ marginTop: 0 }}>อัพโหลด PDF (ปรับยอดได้ เลขที่ใบกำกับภาษีคงเดิม)</h3>
                            <p style={{ margin: "0 0 8px", color: "#666" }}>
                                ไฟล์ PDF ใบกำกับภาษีจริงที่ออกไปแล้ว — ยอด/จำนวนจะถูกปรับตามสัดส่วนยอดขายด้านบนเหมือนข้อมูลจาก DB
                                แต่เลขที่ใบกำกับภาษีจริงจะคงเดิมเสมอ ไม่มีการเปลี่ยนแปลง
                            </p>
                            <input
                                type="file"
                                accept="application/pdf"
                                multiple
                                onChange={handleAdjustableFilesChange}
                            />
                            {adjustableFiles.length > 0 && (
                                <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
                                    {adjustableFiles.map((file, i) => (
                                        <li key={`${file.name}-${i}`} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            <span>{file.name}</span>
                                            <button
                                                type="button"
                                                className="btc-btn btn-sm"
                                                style={{ backgroundColor: "#a33", padding: "2px 8px" }}
                                                onClick={() => removeAdjustableFile(i)}
                                            >
                                                ลบ
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div style={{ flex: "1 1 320px", minWidth: 280 }}>
                            <h3 style={{ marginTop: 0 }}>อัพโหลด PDF (ล็อกทั้งหมด ไม่ปรับอะไรเลย)</h3>
                            <p style={{ margin: "0 0 8px", color: "#666" }}>
                                ไฟล์ PDF ใบกำกับภาษีจริงที่ออกไปแล้ว — จัดรูปแบบใหม่ตามเทมเพลตเท่านั้น เลขที่ใบกำกับภาษี ยอดเงิน
                                และจำนวนคงเดิมทั้งหมด ไม่ถูกปรับด้วยสัดส่วนยอดขาย
                            </p>
                            <input
                                type="file"
                                accept="application/pdf"
                                multiple
                                onChange={handleLockedFilesChange}
                            />
                            {lockedFiles.length > 0 && (
                                <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
                                    {lockedFiles.map((file, i) => (
                                        <li key={`${file.name}-${i}`} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            <span>{file.name}</span>
                                            <button
                                                type="button"
                                                className="btc-btn btn-sm"
                                                style={{ backgroundColor: "#a33", padding: "2px 8px" }}
                                                onClick={() => removeLockedFile(i)}
                                            >
                                                ลบ
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="btc-card" style={{ padding: "16px 24px" }}>
                <p style={{ margin: 0 }}>
                    สร้าง PDF ใบกำกับภาษีอย่างย่อ 1 ใบต่อรายงาน (1 report = 1 เที่ยวรถ/รอบ) สำหรับทุกรายงานในช่วงวันที่ที่เลือก
                    รวมกับใบกำกับภาษีจริงที่อัพโหลด (ถ้ามี) จัด 3 ใบต่อแผ่น A4 แนวนอน พร้อมพิมพ์และตัดแยก
                    ลำดับในไฟล์: ไฟล์ล็อกทั้งหมด → ไฟล์ปรับยอดได้ → รายการจากฐานข้อมูล (เรียงเลขที่ใบกำกับภาษีจากน้อยไปมากในแต่ละกลุ่ม)
                    ไฟล์จะดาวน์โหลดอัตโนมัติเมื่อสร้างเสร็จ
                    เว้นว่างสัดส่วนยอดขายไว้เพื่อใช้ข้อมูลเต็มจำนวน หรือใส่ค่า 0.1-1.0 เพื่อจำลองยอดขายบางส่วน
                    หน้าแรกของไฟล์เป็นหน้าสรุปยอดรวมทั้งชุด พร้อมจำนวนกระสอบรวมและน้ำหนักรวม (1 กระสอบ = 20 กก.) โดยไม่นับรายการขายกระสอบเปล่า
                </p>
            </div>
        </div>
    );
}
